import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"

interface Person {
  id: string
  first_name: string
  last_name: string
  current_client_id?: string
  email?: string
}

interface SimpleMultiSelectProps {
  people: Person[]
  selectedPeopleIds: string[]
  onSelectionChange: (selectedIds: string[]) => void
  placeholder?: string
  clientFilter?: string
}

export function SimpleMultiSelect({
  people,
  selectedPeopleIds,
  onSelectionChange,
  placeholder = "Select people...",
  clientFilter
}: SimpleMultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState("")
  
  // Ensure people and selectedPeopleIds are always arrays
  const safePeople = Array.isArray(people) ? people : []
  const safeSelectedIds = Array.isArray(selectedPeopleIds) ? selectedPeopleIds : []
  
  // Filter people by client and search term
  const filteredPeople = safePeople.filter(person => {
    const matchesClient = !clientFilter || person.current_client_id === clientFilter
    const fullName = `${person.first_name} ${person.last_name}`.trim()
    const displayName = fullName || person.email || ''
    const matchesSearch = !searchTerm || 
      displayName.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesClient && matchesSearch
  })

  const selectedPeople = safePeople.filter(person => safeSelectedIds.includes(person.id))

  const handleSelect = (personId: string) => {
    if (safeSelectedIds.includes(personId)) {
      onSelectionChange(safeSelectedIds.filter(id => id !== personId))
    } else {
      onSelectionChange([...safeSelectedIds, personId])
    }
  }

  const handleRemove = (personId: string) => {
    onSelectionChange(safeSelectedIds.filter(id => id !== personId))
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between bg-background"
          >
            {selectedPeople.length > 0
              ? `${selectedPeople.length} selected`
              : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 bg-background z-50" align="start">
          <div className="bg-background rounded-md">
            {/* Search input */}
            <div className="p-2 border-b pointer-events-auto">
              <Input
                placeholder="Search people..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8"
              />
            </div>
            
            {/* People list with ScrollArea */}
            <ScrollArea className="h-64">
              <div className="p-1">
                {filteredPeople.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No people found.
                  </div>
                ) : (
                  filteredPeople.map((person) => (
                      <div
                        key={person.id}
                        className="flex items-center px-3 py-2 cursor-pointer hover:bg-muted rounded-sm pointer-events-auto"
                        onClick={() => handleSelect(person.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            safeSelectedIds.includes(person.id) ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {person.first_name || person.last_name 
                          ? `${person.first_name} ${person.last_name}`.trim()
                          : person.email || `Person ${person.id.slice(0, 8)}`}
                      </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>
      
      {/* Selected people badges */}
      {selectedPeople.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedPeople.map((person) => (
            <Badge
              key={person.id}
              variant="secondary"
              className="text-xs px-2 py-1"
            >
              {person.first_name || person.last_name 
                ? `${person.first_name} ${person.last_name}`.trim()
                : person.email || `Person ${person.id.slice(0, 8)}`}
              <button
                type="button"
                onClick={() => handleRemove(person.id)}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}